import * as LodashFP from 'lodash/fp'
import { toast } from 'react-toastify'

import { decode, doHttp } from '../../utils/json-api'
import adapters from '../../services/api/adapters'
import {
  FormBlueprint,
  FormChangeHandler,
  FormValue,
  Option,
} from '../../interfaces/components/dynamic-form'

import { HttpCommand } from '../../interfaces/base'
import { getValidationMessages } from '../../utils/error'

import {
  getBlueprintAtPath,
  initialize,
  updateBlueprintOptions,
  walkBlueprint,
  removeUnusedValuesFromObject,
} from './helpers'
import { flattenObject, unflattenObject } from '../../utils/object'
import { IStringAnyMap } from '../../types/map'

const getNewBlueprint = (
  data: any,
  blueprint: FormBlueprint,
  meta: { objectKey: string; originalBlueprint: FormBlueprint },
) => {
  const blueprintFromResponse =
    LodashFP.getOr(null, ['meta', 'blueprint'], data) ||
    LodashFP.getOr(null, ['data', 'blueprint'], data)
  const previousBlueprint = blueprintFromResponse
    ? decode(adapters.blueprint, blueprintFromResponse, 'default', {
        built: true,
        name: blueprintFromResponse.name || 'form-blueprint',
      })
    : blueprint

  const object =
    LodashFP.getOr(
      null,
      ['data', 'next_possible_values', meta.objectKey],
      data,
    ) ||
    LodashFP.getOr(null, ['meta', 'next_possible_values', meta.objectKey], data)

  const flatObj: IStringAnyMap = flattenObject(object ?? {})

  const keys = Object.keys(flatObj)
  const options = Object.assign(
    {},
    ...Array.from(keys, (key) => {
      const blueprintAtPath = getBlueprintAtPath(meta.originalBlueprint, key)

      return {
        [key]:
          flatObj[key].map &&
          flatObj[key].map((val: string) => {
            const originalBlueprintOptions = LodashFP.getOr(
              null,
              'meta.options',
              blueprintAtPath,
            )

            const optionLabel =
              originalBlueprintOptions !== null
                ? (
                    originalBlueprintOptions.find(
                      (originalOption: Option) => originalOption.value === val,
                    ) || { label: val }
                  ).label
                : val
            return { label: optionLabel, value: val }
          }),
      }
    }),
  )
  return updateBlueprintOptions(options, previousBlueprint)
}

const cleanupValue = (
  value: FormValue,
  blueprint: FormBlueprint,
): IStringAnyMap => {
  const valuesFromBlueprint = getNextPossibleValuesFromBlueprint(blueprint)
  return getCleanedupValue(value, valuesFromBlueprint)
}

const getNextPossibleValuesFromBlueprint = (blueprint: FormBlueprint) => {
  const values: any = Object.assign(
    {},
    ...Array.from(blueprint.fields, (field) => {
      if (field.type === 'fieldset') {
        return { [field.name]: getNextPossibleValuesFromBlueprint(field) }
      }
      if (field.type === 'select') {
        const options = LodashFP.getOr([], 'meta.options', field).filter(
          (opt: Option) => !opt.disabled,
        )
        return {
          [field.name]: options.map((option: Option) => option.value),
        }
      }

      return undefined
    }),
  )
  return values
}

const getCleanedupValue = (
  ivalue: FormValue,
  valuesFromBlueprint: FormValue,
) => {
  const value = LodashFP.cloneDeep(ivalue)
  Object.keys(value).forEach((key) => {
    if (
      LodashFP.isArray(valuesFromBlueprint[key]) &&
      LodashFP.isArray(value[key])
    ) {
      value[key] = LodashFP.intersection(valuesFromBlueprint[key], value[key])
    } else if (
      LodashFP.isObject(valuesFromBlueprint[key]) &&
      LodashFP.isObject(value[key])
    ) {
      value[key] = getCleanedupValue(value[key], valuesFromBlueprint[key])
    } else if (
      LodashFP.isArray(valuesFromBlueprint[key]) &&
      value[key] &&
      !valuesFromBlueprint[key].includes(value[key])
    ) {
      value[key] = []
    }
  })
  return value
}

const isValid = (input: any) => {
  return LodashFP.getOr(true, 'valid', input)
}

function coerceDataToBlueprint(value: IStringAnyMap, blueprint: FormBlueprint) {
  const flat = flattenObject(value)
  const keysToRemove = [] as string[]
  Object.keys(flat).forEach((key) => {
    const blueprintForKey = getBlueprintAtPath(blueprint, key)
    if (blueprintForKey == null) {
      keysToRemove.push(key)
    }
  })
  keysToRemove.forEach((key) => {
    delete flat[key]
  })

  const keys = Object.keys(flat)
  walkBlueprint(blueprint, (bp, path) => {
    if (!keys.includes(path) && bp.type !== 'fieldset') {
      flat[path] = initialize(bp)
    }
  })

  const final = unflattenObject(flat)

  return final
}

export const BlueprintChangeHandler = (
  originalBlueprint: FormBlueprint,
  changeHttpCmd: (value: any) => HttpCommand,
  objectKey: string,
): FormChangeHandler => {
  const changeHandler: FormChangeHandler = async (blueprint, value) => {
    const validateResponse = await doHttp(changeHttpCmd(value), {
      throwOnError: false,
      expectedErrorCodes: [422, 400],
    })

    if ('errors' in validateResponse.data) {
      toast.error(
        LodashFP.flattenDeep(validateResponse.data.errors).join(','),
        {
          autoClose: false,
        },
      )
    }

    const newBlueprint = getNewBlueprint(validateResponse, blueprint, {
      objectKey,
      originalBlueprint,
    })
    const newValue = cleanupValue(value, newBlueprint)
    const valueChanged = !LodashFP.isEqual(value, newValue)

    if (valueChanged) {
      return changeHandler(newBlueprint, newValue)
    }

    const nextValue = coerceDataToBlueprint(newValue, newBlueprint)

    const cleanValue = removeUnusedValuesFromObject('creatives[x]', nextValue)

    return {
      value: cleanValue,
      blueprint: newBlueprint,
      validation: {
        validUntil: isValid(validateResponse.data)
          ? 'end'
          : validateResponse.data.valid_before_step,
        fields: getValidationMessages(
          [
            ...LodashFP.getOr([], 'data.messages', validateResponse),
            ...LodashFP.getOr([], 'meta.messages', validateResponse),
          ],
          getNewBlueprint(validateResponse, blueprint, {
            objectKey,
            originalBlueprint,
          }),
        ),
      },
    }
  }

  return changeHandler
}

export default BlueprintChangeHandler
